/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { NumberBox as DXNumberBox } from 'devextreme-react/number-box';
import { Validator, RequiredRule, CustomRule } from 'devextreme-react/validator';
import '../../../scss/controls/_numberbox.scss';
import { KeyUpEvent, ValueChangedEvent } from 'devextreme/ui/number_box';
import { ValidatedEvent } from 'devextreme/ui/validator';

interface NumberBoxProps {
    id?: string;
    dataTestid?: string;
    value?: number;
    allowNegative?: boolean;
    format?: string;
    readOnly?: boolean;
    disabled?: boolean;
    label?: string;
    helperText?: React.ReactNode;
    required?: boolean;
    children?: React.ReactNode;
    validator?: React.ReactNode;
    width?: string;
    max?: number;
    mask?: string;
    maskInvalidMessage?: string;
    onChange: (value: number) => void;
    onEnterKey?: () => void;
    onKeyUp?: ((e: KeyUpEvent) => void) | undefined;
    validateValue?: (value: string) => boolean;
    onValidationEvent?: (isValid: boolean) => void;
    validateValueMessage?: string;
}

// Allowing "any" because it is defined by DevExtreme
export interface ValidationCallbackOptions {
    column: Record<string, any>;
    data: Record<string, any>;
    formItem: Record<string, any>;
    rule: Record<string, any>;
    validator: Record<string, any>;
    value: string | number;
}

export const NumberBox: React.FC<NumberBoxProps> = ({
    id,
    dataTestid,
    value,
    allowNegative = false,
    format = '#',
    mask,
    maskInvalidMessage,
    readOnly = false,
    disabled = false,
    label,
    helperText,
    required = false,
    children,
    validator,
    width = 'auto',
    max,
    onChange,
    onEnterKey,
    validateValue,
    onValidationEvent,
    validateValueMessage,
}) => {
    const [valueChangeEvent, set_valueChangeEvent] = useState('change');
    const [errorMessage, set_errorMessage] = useState('');

    const handleValueChanged = (e: ValueChangedEvent) => {
        onChange(e.value);
        set_valueChangeEvent('input');
    };

    const onValidated = (validatedInfo: ValidatedEvent) => {
        set_errorMessage(validatedInfo?.brokenRule?.message ?? '');
    };

    const validationCallback = (options: ValidationCallbackOptions): boolean => {
        if (!validateValue) return true;

        const result = validateValue(options.value.toString()) ?? false;

        if (onValidationEvent) onValidationEvent(result);

        return result;
    };

    const onKeyUp = (e: any) => {
        if (!validateValue) return;
        if (!onValidationEvent) return;

        const value = e.event.currentTarget.value;

        const isValid: boolean = validateValue(value);
        onValidationEvent(isValid);
    };

    return (
        <div
            className='numberbox-container'
            style={{ width: width }}
        >
            {label && <label className={`numberbox-label ${errorMessage ? 'error' : ''}`}>{label}</label>}
            <div
                className='numberbox-wrapper'
                style={{ position: 'relative' }}
            >
                <DXNumberBox
                    id={id}
                    data-testid={dataTestid}
                    mode='number'
                    format={format}
                    mask={mask}
                    maskInvalidMessage={maskInvalidMessage}
                    value={value}
                    valueChangeEvent={valueChangeEvent}
                    disabled={disabled}
                    onValueChanged={handleValueChanged}
                    onKeyUp={onKeyUp}
                    readOnly={readOnly}
                    className={`numberbox-input ${errorMessage ? 'error' : ''}`}
                    width={width}
                    min={!allowNegative ? 0 : undefined}
                    max={max}
                    isValid={true}
                    onEnterKey={onEnterKey}
                >
                    <Validator onValidated={onValidated}>
                        {required && <RequiredRule message='This field is required' />}
                        {validator}
                        {validateValue && validationCallback && (
                            <CustomRule
                                validationCallback={validationCallback}
                                message={validateValueMessage}
                            />
                        )}
                    </Validator>
                </DXNumberBox>
            </div>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='numberbox-error'>{errorMessage}</div>
            ) : (
                helperText && <div className='numberbox-helper'>{helperText}</div>
            )}
            {children && <div className='numberbox-children'>{children}</div>}
        </div>
    );
};
